import { memo, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isServer, services } from '../../globals';
import { Metadata } from '../../shared/models/metadata.model';
import { BasefileFunction, Site } from '../../shared/models/site.model';
import { getBasefilePathByFunction } from '../../shared/utils/basefile.utils';
import { getErrorPagePath } from '../../shared/path-resolve.utils';
import { BookActionName, useBookAction, useBookPath } from '../../state/BookContext';
import { FileActionName, useFileAction, usePath } from '../../state/FileContext';
import { useBaselineFilter, useLangFilter, useSystemPropertiesFilter, useValidityFilter } from '../../state/FilterContext';
import { InternalActionName, useInternalAction } from '../../state/InternalContext';
import { useParser } from '../../state/parser-hook';
import { useSiteContext } from '../../state/SiteContext';
import { mapMetadataToItem } from '../../shared/utils/source-item.utils';
import { useAuth } from './AuthProvider';
import SetPassword from './SetPassword';
import { isSetPasswordPath } from '../../shared/utils/set-password-path';

interface SiteRootProps {
  indexcontent: string;
}

/***
 * This is the top level component that renders the entire site.
 */
export const SiteRoot = memo(function SiteRoot({ indexcontent }: SiteRootProps) {
  const isFirstRender = useRef(true);
  const awaitingPathSync = useRef(false);
  const previousLocationPathname = useRef<string>("");
  const previousFilePath = useRef<string>("");
  const { siteState } = useSiteContext();
  const login = useAuth();
  const siteRef = useRef(siteState.site);
  const showLoginRef = useRef(login.showLogin);

  const parse = useParser();

  const validity = useValidityFilter();
  const baseline = useBaselineFilter();
  const systemProperties = useSystemPropertiesFilter();
  const lang = useLangFilter();
  const hasUser = Boolean(login.user);
  const allowGuestAccess = siteState.site?.auth?.allowGuestAccess;
  const siteName = siteState.site?.name || "";

  siteRef.current = siteState.site;
  showLoginRef.current = login.showLogin;

  const updatePath = useFileAction(FileActionName.UPDATE_PATH);
  const updateContent = useFileAction(FileActionName.UPDATE_CONTENT);
  const updateBaselineContent = useFileAction(FileActionName.UPDATE_BASELINE_CONTENT);
  const updateMetadata = useFileAction(FileActionName.UPDATE_METADATA);
  const updateBookContent = useBookAction(BookActionName.UPDATE_CONTENT);
  const updateBookMetadata = useBookAction(BookActionName.UPDATE_METADATA);
  const bookPath = useBookPath() || "";
  const setStoredItem = useInternalAction(InternalActionName.SET_STORED_ITEM);

  /***
   * Find out if the site is embedded in an iframe.
   * This is used to apply different styles to the site when it is embedded.
   */
  const inIframe: boolean = (!isServer && window.self !== window.top);

  const location = useLocation();
  const path = usePath() || "";

    useEffect(() => {
    let mounted = true;

    if (isSetPasswordPath(path)) {
      isFirstRender.current = false;
      awaitingPathSync.current = false;
      previousLocationPathname.current = location.pathname;
      previousFilePath.current = path;
      return;
    }

    // Skip API calls on first render - content is already available from SSR
    if (isFirstRender.current) {
      isFirstRender.current = false;
      awaitingPathSync.current = false;
      previousLocationPathname.current = location.pathname;
      previousFilePath.current = path;
      if (allowGuestAccess === false && !hasUser) {
        showLoginRef.current(true); //force login if guest access is disabled
      }
      return;
    }

    const locationChanged = previousLocationPathname.current !== location.pathname;
    if (locationChanged) {
      previousLocationPathname.current = location.pathname;
      awaitingPathSync.current = true;
    }

    // During Link navigation, URL can update before FileContext.path catches up.
    // Keep blocking fetches until the file path actually changes from the previous path.
    if (awaitingPathSync.current) {
      const pathHasCaughtUp = path !== previousFilePath.current;
      if (!pathHasCaughtUp) {
        return;
      }
      awaitingPathSync.current = false;
    }

    previousFilePath.current = path;

    const contentUrl = services.file.getDirectLink(siteName,
      path, validity, systemProperties, lang) || "";
    const baselineContentUrl = baseline ? services.file.getDirectLink(siteName,
      path, baseline, systemProperties, lang) || "" : "";
    const metadataUrl = services.file.getDirectLink(siteName,
      path, validity, systemProperties, lang, "metadata") || "";
    const bookContentUrl = bookPath ? services.file.getDirectLink(siteName,
      bookPath, validity, systemProperties, lang) || "" : "";
    const bookMetadataUrl = bookPath ? services.file.getDirectLink(siteName,
      bookPath, validity, systemProperties, lang, "metadata") || "" : "";

    let content: string | null = null;
    let baselineContent: string | null = null;
    let metadata: Metadata | null = null;
    let bookContent: string | null = null;
    let bookMetadata: Metadata | null = null;

    const fetchContent = async () => {
      if (contentUrl) {
        const result = await services?.file.getTextfile(contentUrl || "");
        content = result;
      }
    };

    const fetchBaselineContent = async () => {
      if (baselineContentUrl) {
        const result = await services?.file.getTextfile(baselineContentUrl || "");
        baselineContent = result;
      }
    };

    const fetchMetadata = async () => {
      if (metadataUrl) {
        const result = await services?.file.getMetadataByUrl(metadataUrl || "");
        metadata = result;
      }
    };
    const fetchBookContent = async () => {
      if (bookContentUrl) {
        bookContent = await services?.file.getTextfile(bookContentUrl);
      }
    };
    const fetchBookMetadata = async () => {
      if (bookMetadataUrl) {
        bookMetadata = await services?.file.getMetadataByUrl(bookMetadataUrl);
      }
    };

    Promise.all([fetchContent(), fetchBaselineContent(), fetchMetadata(), fetchBookContent(), fetchBookMetadata()]).then(() => {
      if (mounted) {
        updateContent(content || undefined);
        updateBaselineContent(baselineContent || undefined);
        updateMetadata(metadata || undefined);
        updateBookContent(bookContent || undefined);
        updateBookMetadata(bookMetadata || undefined);
        setStoredItem({
          _metadata: metadata ? { ...mapMetadataToItem(metadata), position: 1 } : undefined,
          _book: bookMetadata ? { ...mapMetadataToItem(bookMetadata), position: 1 } : undefined,
        })
      }
    }).catch(err => {
      if (err.message === "Unauthorized") {
        updatePath(getBasefilePathByFunction(siteRef.current, BasefileFunction.UNAUTHORIZED_PAGE) || "/home.html");
        showLoginRef.current();
      } else if (err.message === "Forbidden") {
        updatePath(getBasefilePathByFunction(siteRef.current, BasefileFunction.FORBIDDEN_PAGE) || "/home.html");
      }
    });
    return () => {
      mounted = false;
    };
  }, [
    path,
    location.pathname,
    siteName,
    allowGuestAccess,
    hasUser,
    validity,
    baseline,
    bookPath,
    systemProperties,
    lang,
    updateContent,
    updateBaselineContent,
    updateMetadata,
    updateBookContent,
    updateBookMetadata,
    updatePath,
    setStoredItem]);

  const pageContent = useMemo(() => parse(indexcontent), [indexcontent, parse]);

  /**
   * Checks if the current path matches any of the site's error pages.
   * Error pages should be rendered normally even when there's an auth error.
   * 
   * @param filepath The file path to check.
   * @param files Site files configuration containing error page paths.
   * @returns True if the path matches an error page.
   */
  const isErrorPagePath = (filepath: string, files?: Site["files"]): boolean => {
    if (!files || !filepath) return false;
    const keys = ["guestNotAllowedPage", "unauthorizedPage", "forbiddenPage", "missingPage"] as const;
    const errorPagePaths = keys
      .map((key) => getErrorPagePath(files, key))
      .filter((p): p is string => Boolean(p));
    return errorPagePaths.some(
      (errorPath) => filepath === errorPath || filepath.endsWith(errorPath)
    );
  };

  const isOnErrorPage = isErrorPagePath(path, siteState.site?.files);

  const renderContent = () => {
    if (isSetPasswordPath(path)) {
      return <SetPassword />;
    }
    console.log("login.authErrorStatus", login.authErrorStatus, "isOnErrorPage", isOnErrorPage);
    if (login.authErrorStatus === 401 && !isOnErrorPage) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Authentication Required</h2>
          <p>Please log in to access this content.</p>
        </div>
      );
    }
    else if (login.authErrorStatus === 403 && !isOnErrorPage) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Access Forbidden</h2>
          <p>You don't have permission to access this content.</p>
        </div>
      );
    } else {
      return pageContent || "";
    }
  }

  return (
    <div
      id={"__scdp_siteroot_component"}
      className={"_site " + (inIframe ? " embedded" : " standalone")}>
      {renderContent()}
    </div>
  );
});
